import { Css, Step, useToast } from "@homebound/beam";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { StepperProvider, stepperBarHeight, useStepperContext } from "src/components/stepper";
import { ContractType } from "src/generated/graphqlTypes";
import { useHiddenProjectSideNav } from "src/hooks";
import { useRouteParams } from "src/hooks/useRouteParams";
import { IdOrAddParams, ProjectParams } from "src/routes/routesDef";
import { createInvoicesUrl } from "src/RouteUrls";
import { StageUrlSlug, getStageFromSlug, hasData, renderLoadingOrError } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";
import { InvoiceStepEnum } from "./enums";
import { ConfirmDrawAmount } from "./invoice-steps/ConfirmDrawAmount";
import { ConfirmLineItems } from "./invoice-steps/ConfirmLineItems";
import { InvoiceSettings } from "./invoice-steps/InvoiceSettings";
import { ReviewInvoice } from "./invoice-steps/ReviewInvoice";
import { SelectFixedItems } from "./invoice-steps/SelectFixedItems";
import {
  InvoiceCreatePage_InvoiceFragment,
  InvoiceProjectData_ProjectFragment,
  InvoiceProjectStageFragment,
  useInvoiceCreatePageQuery,
  useInvoiceProjectDataQuery,
} from "./InvoiceCreatePage.generated";

export default function InvoiceCreatePage() {
  useHiddenProjectSideNav();

  return (
    <StepperProvider steps={initialSteps}>
      <InvoiceCreatePageQueryWrapper />
    </StepperProvider>
  );
}

function InvoiceCreatePageQueryWrapper() {
  const { projectId, idOrAdd } = useRouteParams<ProjectParams & IdOrAddParams>();
  const [{ stageSlug }] = useQueryParams({ stageSlug: StringParam });
  const navigate = useNavigate();
  const { showToast } = useToast();
  const isNew = idOrAdd === "add";
  const invoiceProjectDataQuery = useInvoiceProjectDataQuery({ variables: { projectId } });
  const invoiceQuery = useInvoiceCreatePageQuery({ variables: { id: idOrAdd! }, skip: isNew || !idOrAdd });

  // in case the user access directly the page without stageSlug param
  const redirectToInvoices = isNew && !stageSlug;
  useEffect(() => {
    if (redirectToInvoices) {
      showToast({ type: "error", message: "You can't create a new invoice without pre select the project stage" });
      navigate(createInvoicesUrl(projectId));
    }
  }, [redirectToInvoices, showToast, navigate, projectId]);

  if (!idOrAdd || redirectToInvoices) {
    return <></>;
  } else if (!isNew && !hasData(invoiceQuery)) {
    return renderLoadingOrError(invoiceQuery);
  } else if (!hasData(invoiceProjectDataQuery)) {
    return renderLoadingOrError(invoiceProjectDataQuery);
  } else {
    return (
      <InvoiceCreatePageView invoice={invoiceQuery.data?.invoice} project={invoiceProjectDataQuery.data.project} />
    );
  }
}

type InvoiceCreatePageViewProps = {
  invoice?: InvoiceCreatePage_InvoiceFragment;
  project: InvoiceProjectData_ProjectFragment;
};

function InvoiceCreatePageView({ invoice, project }: InvoiceCreatePageViewProps) {
  const navigate = useNavigate();
  const { currentStep, setSteps, goToStep } = useStepperContext();
  const { projectId } = useRouteParams<ProjectParams & IdOrAddParams>();
  const [{ stageSlug }] = useQueryParams({ stageSlug: StringParam });

  const [draftInvoice, setDraftInvoice] = useState<InvoiceCreatePage_InvoiceFragment | undefined>(invoice);

  useEffect(() => {
    // Store the loaded invoice in state so downstream steps edit a draft copy.
    if (invoice) {
      setDraftInvoice(invoice);
    }
    const projectStage = resolveInvoiceStepStage(invoice, stageSlug, project);
    const hasCostPlusLineItems = hasLineItemsWithContractType(invoice, ContractType.CostPlus);
    const hasFixedLineItems = hasLineItemsWithContractType(invoice, ContractType.Fixed);

    setSteps(buildInvoiceSteps({ invoice, projectStage, hasCostPlusLineItems, hasFixedLineItems }));

    const initialStep = resolveInitialStep({ invoice, hasCostPlusLineItems, hasFixedLineItems });
    if (initialStep) {
      goToStep(initialStep);
    }
    // TODO: validate this eslint-disable. It was automatically ignored when the oxlint migration restored `react-hooks/exhaustive-deps` to an error.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, project]);

  const newestInvoiceDate = useMemo(() => {
    const projectStages = project.stages;
    if (!projectStages || !draftInvoice) {
      return null;
    }

    const currentStage = projectStages.find((ps) => ps.id === draftInvoice!.projectStage.id);
    const invoiceDates = (currentStage?.invoices || [])
      .filter((inv) => inv.id !== draftInvoice!.id)
      .sort((a, b) => (a.invoiceDate.equals(b.invoiceDate) ? 0 : a.invoiceDate.isBefore(b.invoiceDate) ? -1 : 1));
    return invoiceDates.last ? invoiceDates.last.invoiceDate.add({ days: 1 }) : project.startDate;
  }, [draftInvoice, project]);

  const exit = () => navigate(createInvoicesUrl(projectId));

  let projectStage: InvoiceProjectStageFragment;
  if (stageSlug) {
    const stageParam = getStageFromSlug(stageSlug as StageUrlSlug);
    projectStage = project.stages.find((s) => s.stage.code === stageParam)!;
  }

  const renderStep = () => {
    switch (currentStep.value) {
      case InvoiceStepEnum.SELECT_DRAW_AMOUNT:
        return (
          <ConfirmDrawAmount
            onExit={exit}
            setDraftInvoice={setDraftInvoice}
            draftInvoice={draftInvoice!}
            startDate={newestInvoiceDate!}
          />
        );
      case InvoiceStepEnum.SELECT_COST_PLUS_ITEM:
        return (
          <ConfirmLineItems
            onExit={exit}
            setDraftInvoice={setDraftInvoice}
            invoice={draftInvoice!}
            startDate={newestInvoiceDate!}
          />
        );
      case InvoiceStepEnum.SELECT_FIXED_ITEM:
        return <SelectFixedItems onExit={exit} setDraftInvoice={setDraftInvoice} invoice={draftInvoice!} />;
      case InvoiceStepEnum.REVIEW:
        return <ReviewInvoice lineItemStartDate={newestInvoiceDate!} onExit={exit} draftInvoice={draftInvoice!} />;
      default:
        return (
          <InvoiceSettings
            projectStage={projectStage}
            setDraftInvoice={setDraftInvoice}
            draftInvoice={draftInvoice}
            onExit={exit}
            project={project}
          />
        );
    }
  };

  return <div css={Css.mbPx(stepperBarHeight).$}>{renderStep()}</div>;
}

const initialSteps: Step[] = [
  { label: "Invoice settings", disabled: false, state: "incomplete" as const, value: InvoiceStepEnum.SETTINGS },
  { label: "Select draw amounts", disabled: true, state: "incomplete", value: InvoiceStepEnum.SELECT_DRAW_AMOUNT },
  { label: "Select Cost+ items", disabled: true, state: "incomplete", value: InvoiceStepEnum.SELECT_COST_PLUS_ITEM },
  { label: "Select Fixed items", disabled: true, state: "incomplete", value: InvoiceStepEnum.SELECT_FIXED_ITEM },
  { label: "Review invoice", disabled: true, state: "incomplete", value: InvoiceStepEnum.REVIEW },
];

// The stage that drives which steps to show: the invoice's own stage when editing, otherwise the
// stage picked via the `stageSlug` query param when creating a brand new invoice.
function resolveInvoiceStepStage(
  invoice: InvoiceCreatePage_InvoiceFragment | undefined,
  stageSlug: string | null | undefined,
  project: InvoiceProjectData_ProjectFragment,
) {
  if (invoice) {
    return invoice.projectStage;
  }
  if (stageSlug) {
    const stageParam = getStageFromSlug(stageSlug as StageUrlSlug);
    return project.stages.find((s) => s.stage.code === stageParam);
  }
  return undefined;
}

function hasLineItemsWithContractType(
  invoice: InvoiceCreatePage_InvoiceFragment | undefined,
  contractType: ContractType,
): boolean {
  return (invoice?.lineItems || []).some((li) => li.homeownerContractLineItem.owner.contractType === contractType);
}

type InvoiceStepInputs = {
  invoice: InvoiceCreatePage_InvoiceFragment | undefined;
  projectStage: ReturnType<typeof resolveInvoiceStepStage>;
  hasCostPlusLineItems: boolean;
  hasFixedLineItems: boolean;
};

// Builds the stepper steps, only including the draw/cost/fixed steps the stage actually invoices for.
function buildInvoiceSteps(args: InvoiceStepInputs): Step[] {
  const { invoice } = args;
  const disabled = !invoice;
  return [
    { ...initialSteps[0], disabled: false, state: invoice ? "complete" : "incomplete" },
    ...optionalInvoiceSteps(args).map(
      ({ step, complete }): Step => ({ ...step, disabled, state: complete ? "complete" : "incomplete" }),
    ),
    { ...initialSteps[4], disabled: !hasAnyLineItems(invoice), state: "incomplete" },
  ];
}

// The draw/cost/fixed steps the stage actually invoices for, each with whether it already has line items.
function optionalInvoiceSteps(args: InvoiceStepInputs): { step: Step; complete: boolean }[] {
  const { invoice, projectStage, hasCostPlusLineItems, hasFixedLineItems } = args;
  const candidates = [
    { include: projectStage?.hasDrawsToInvoice, step: initialSteps[1], complete: !!invoice?.drawLineItems?.length },
    { include: projectStage?.hasCostsToInvoice, step: initialSteps[2], complete: hasCostPlusLineItems },
    { include: projectStage?.hasFixedToInvoice, step: initialSteps[3], complete: hasFixedLineItems },
  ];
  return candidates.filter((c) => c.include);
}

function hasAnyLineItems(invoice: InvoiceCreatePage_InvoiceFragment | undefined): boolean {
  return !!(invoice?.drawLineItems?.length || invoice?.lineItems?.length);
}

// Picks the step to land on: fixed items first, then cost+, then draws; otherwise stay on settings.
function resolveInitialStep(args: {
  invoice: InvoiceCreatePage_InvoiceFragment | undefined;
  hasCostPlusLineItems: boolean;
  hasFixedLineItems: boolean;
}): InvoiceStepEnum | undefined {
  const { invoice, hasCostPlusLineItems, hasFixedLineItems } = args;
  if (hasFixedLineItems) {
    return InvoiceStepEnum.SELECT_FIXED_ITEM;
  }
  if (hasCostPlusLineItems) {
    return InvoiceStepEnum.SELECT_COST_PLUS_ITEM;
  }
  if (invoice && invoice.drawLineItems.length > 0) {
    return InvoiceStepEnum.SELECT_DRAW_AMOUNT;
  }
  return undefined;
}
